import { MdCheck, MdClose } from "react-icons/md";
import Modal from "../Modal/Modal";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import CheckBox from "../CheckBox/CheckBox";
import useMobile from "../../hooks/useMobile";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface IConfirmDialog {
  title: string;
  onClose: () => void;
  onConfirm: () => void;
  description: string;
  confirmKind?: EButtonKind;
  isLoading?: boolean;
  cancelLabel?: string;
  showCancel?: boolean;
  note?: string;
  doubleConfirmRequired?: boolean;
}

const ConfirmDialog: React.FC<IConfirmDialog> = ({
  title,
  onClose,
  onConfirm,
  description,
  confirmKind = EButtonKind.SAVE,
  isLoading = false,
  cancelLabel = "",
  showCancel = true,
  note = "",
  doubleConfirmRequired = false,
}) => {
  const { t } = useTranslation(["common", "misc"]);
  const { isMobile } = useMobile();

  const [doubleConfirm, setDoubleConfirm] = useState<boolean>(
    doubleConfirmRequired ? false : true,
  );

  const actionIsDisabled = useMemo(() => {
    if (doubleConfirmRequired) {
      return !doubleConfirm || isLoading;
    }

    return isLoading;
  }, [isLoading, doubleConfirmRequired, doubleConfirm]);

  return (
    <Modal
      width="md:!max-w-[520px] md:!min-w-[400px]"
      onEscape={() => {
        if (!isLoading && showCancel) {
          onClose();
        }
      }}
      className="!shadow"
      closeOnOverlayClick={!isLoading && showCancel}
    >
      <Modal.Header
        onClose={() => {
          if (!isLoading && showCancel) {
            onClose();
          }
        }}
        isClosable={showCancel}
        title={title}
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "w-full", "gap-4"])}>
          <div className={clsx(["flex", "w-full", "flex-col", "gap-2"])}>
            <p className={clsx(["text-xs", "md:text-sm"])}>{description}</p>
            {note && <p className={clsx(["warning-text"])}>{note}</p>}
          </div>
          {doubleConfirmRequired && (
            <div className={clsx(["flex", "flex-row", "w-full"])}>
              <CheckBox
                disabled={isLoading}
                checked={doubleConfirm}
                label={t("misc:strings.i_agree")}
                onChange={(isChecked: boolean) => setDoubleConfirm(isChecked)}
              />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            kind: confirmKind,
            onClick: onConfirm,
            text: t("common:buttons.confirm"),
            icon: <MdCheck size={ICON_SIZES.SM} />,
            isLoading,
            disabled: actionIsDisabled,
            autoFocus: true,
            tooltip: doubleConfirm ? "" : t("common:tooltips.double_confirm"),
          },
          {
            kind: EButtonKind.NORMAL,
            onClick: onClose,
            text: cancelLabel || t("common:buttons.cancel"),
            icon: <MdClose size={ICON_SIZES.SM} />,
            disabled: isLoading,
            hide: !showCancel,
          },
        ]}
      />
    </Modal>
  );
};

export default memo(ConfirmDialog);
